import { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../Context'
import { AuthContextApi } from '../../Context/api'
import {
    CalendarDaysIcon,
    PlusIcon,
    TrashIcon,
    ArrowRightIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
} from '@heroicons/react/24/outline'
import Header from '../../Components/Header'
import ModalDataExibicao from '../../Components/Modal/DataExibicao'

const PER_PAGE = 12

const DatasExibicao = () => {
    const { setters } = useContext(AuthContext)
    const { getDataExibicao, postDataExibicao, deleteDataExibicao } = useContext(AuthContextApi)

    const [datas, setDatas] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [modalOpen, setModalOpen] = useState(false)

    const load = async (targetPage = page) => {
        setLoading(true)
        const data = await getDataExibicao({ page: targetPage, per_page: PER_PAGE, sort: 'data_inicial', sort_dir: 'desc' })
        setDatas(Array.isArray(data) ? data : [])
        setLoading(false)
    }

    useEffect(() => {
        load(page)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page])

    const handleSave = async (data) => {
        const ok = await postDataExibicao(data)
        if (ok) {
            setModalOpen(false)
            setPage(0)
            await load(0)
        }
    }

    const confirmDelete = (item) => {
        setters.setModalMsg(true)
        setters.setPerigoModalMsg(true)
        setters.setMsgModalMsg('Remover esta data de exibição?')
        setters.setDescModalMsg(`O período de ${item.data_inicial} a ${item.data_final} deixará de ficar disponível para o investidor.`)
        setters.setTitleButtonModalMsg('Remover')
        setters.setFunctionModalMsg(() => async () => {
            const ok = await deleteDataExibicao(item.id)
            setters.setModalMsg(false)
            if (ok) await load(page)
        })
    }

    return (
        <>
            <Header
                title="Datas de exibição"
                description="Cadastre os períodos que o investidor poderá visualizar"
                icon={CalendarDaysIcon}
                buttonText="Adicionar data"
                buttonAction={() => setModalOpen(true)}
                hasAction={true}
                hasReturn={false}
                color="yellow"
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-yellow mx-auto"></div>
                            <p className="mt-4 text-gray-600">Carregando datas...</p>
                        </div>
                    </div>
                ) : datas.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="p-6 rounded-3xl bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 inline-block">
                            <CalendarDaysIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma data cadastrada</h3>
                            <p className="text-gray-600 mb-4">Cadastre um período para liberar a visualização ao investidor.</p>
                            <button
                                onClick={() => setModalOpen(true)}
                                className="inline-flex items-center px-4 py-2 bg-gray-900 text-brand-yellow rounded-xl hover:bg-gray-800 transition-all duration-300 font-semibold"
                            >
                                <PlusIcon className="w-4 h-4 mr-2" />
                                Adicionar data
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {datas.map((item) => (
                            <div
                                key={item.id}
                                className="relative bg-white rounded-3xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden"
                            >
                                <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-brand-yellow/10 to-brand-yellow/5 rounded-full -translate-y-12 translate-x-12"></div>

                                <div className="relative p-5">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="p-2 rounded-xl bg-gray-900 shadow-lg">
                                            <CalendarDaysIcon className="w-5 h-5 text-brand-yellow" />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 text-gray-900 font-bold text-sm mb-3">
                                        <span>{item.data_inicial}</span>
                                        <ArrowRightIcon className="w-4 h-4 text-gray-400" />
                                        <span>{item.data_final}</span>
                                    </div>

                                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                                        <ClockIcon className="w-3.5 h-3.5" />
                                        Criado em {item.data_criacao}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {(datas.length > 0 || page > 0) && (
                    <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-4 mt-6">
                        <span className="text-sm text-gray-700">Página {page + 1}</span>
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="w-4 h-4 mr-1" />
                                Anterior
                            </button>
                            <button
                                onClick={() => setPage((p) => p + 1)}
                                disabled={datas.length < PER_PAGE}
                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Próximo
                                <ChevronRightIcon className="w-4 h-4 ml-1" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <ModalDataExibicao
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                color="yellow"
            />
        </>
    )
}

export default DatasExibicao
